import { Injectable } from '@angular/core';
import { PermissionsList } from '../../../shared/common/PermissionList';
import { NavigationItem } from '../../components/pages-container/interfaces/navigationList.interface';
import { routes } from '../../../shared/common/routes.consatnt';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private userPermissions: number[] = [];

  constructor() {
    this.loadPermissions();
  }

  /**
   * Loads permissions from `localStorage` and initializes `userPermissions`.
   */
  private loadPermissions(): void {
    try {
      const userInfo = JSON.parse(localStorage.getItem('currentUser')!);
      this.userPermissions = userInfo?.user_profile.Permissions ?? [];
    } catch (error) {
      console.error('Error loading user permissions:', error);
      this.userPermissions = [];
    }
  }

  /**
   * Checks if the user has a specific permission.
   * @param permission The permission key from `PermissionsList`.
   * @returns `true` if the user has the specified permission, otherwise `false`.
   */
  hasPermission(permission: keyof typeof PermissionsList): boolean {
    const permissionValue = PermissionsList[permission];
    return this.userPermissions.includes(permissionValue);
  }

  /**
   * Checks if the user has any of the provided permissions.
   * @param permissions An array of permission keys from `PermissionsList`.
   * @returns `true` if the user has at least one of the specified permissions, otherwise `false`.
   */
  hasAnyPermission(permissions: Array<keyof typeof PermissionsList>): boolean {
    return permissions.some(permission => this.hasPermission(permission));
  }

  /**
   * Reloads permissions from `localStorage`.
   * Can be used if user permissions are updated during runtime.
   */
  refreshPermissions(): void {
    this.loadPermissions();
  }

  getFirstAccessibleNavigation(
    rawNavigationList: NavigationItem[],
  ): NavigationItem | null {
    let userinfo = JSON.parse(localStorage.getItem('currentUser')!);
    let userPermissions = userinfo?.user_profile?.Permissions ?? [];

    const findFirstAccessible = (
      items: NavigationItem[],
    ): NavigationItem | null => {
      for (const item of items) {
        // Check if the user has access to this item
        if (
          item.permissions?.some(perm =>
            userPermissions?.includes(PermissionsList[perm]),
          )
        ) {
          return item;
        }

        // If the item has children, search recursively
        if (item.children && item.children.length > 0) {
          const found = findFirstAccessible(item.children);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    return findFirstAccessible(rawNavigationList);
  }
  getFirstNavigationUrl(sectionId: number | undefined): string {
    try {
      const index = routes.findIndex(x => x.id == sectionId);
      if (index != -1) {
        const availableRoutes: NavigationItem[] = routes.filter(
          x => x.id == sectionId,
        )[0].children;
        const firstAccessible =
          this.getFirstAccessibleNavigation(availableRoutes);
        return firstAccessible?.routeUrl ?? '';
      }
      return '';
    } catch (error) {
      console.error('Error getting first navigation URL', error);
      return '';
    }
  }
}
